import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  hasMargins,
  calculateActivePage,
  calculateScrollAmount,
} from './helpers';
import Lightbox from 'react-image-lightbox';
import throttle from 'lodash/throttle';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import {
  trackZoomActivate,
  trackZoomClose,
  trackZoomNext,
  trackZoomPrev,
} from '../../../../analytics/category/zoom';
import Icon from 'wonderbly-components/lib/Icon';
import qs from 'qs';
import './ScrollingBook.scss';

class ScrollingBook extends React.Component {
  static propTypes = {
    productId: PropTypes.string.isRequired,
    scrollPosition: PropTypes.number.isRequired,
    imageWidth: PropTypes.number.isRequired,
    pages: PropTypes.array.isRequired,
    hasInsideCovers: PropTypes.bool.isRequired,
    activePage: PropTypes.number.isRequired,
    setScrollPosition: PropTypes.func.isRequired,
    setActivePage: PropTypes.func.isRequired,
  };

  _containerRef = React.createRef();

  state = {
    hasUserScrolled: false,
    isUserScrolling: false,
    zoom: {
      isActive: false,
      currentPage: {},
      nextPage: {},
      prevPage: {},
    },
  };

  handleScroll = () => {
    if (!this.state.hasUserScrolled) {
      this.setState({
        hasUserScrolled: true,
      });
    }

    if (this._containerRef && this._containerRef.current) {
      const props = this.props;
      const activePage = calculateActivePage(
        this._containerRef.current.scrollLeft,
        {
          imageWidth: props.imageWidth,
          hasInsideCovers: props.hasInsideCovers,
          totalPageCount: props.pages.length,
        },
      );
      if (props.pages[activePage]) {
        const activePageId = props.pages[activePage].id;
        if (props.activePage !== activePageId) {
          props.setActivePage(activePageId);
        }
      }
    }
    this.setState({
      isUserScrolling: false,
    });
  };

  _throttleScroll = throttle(this.handleScroll, 50);

  componentDidMount() {
    this._containerRef.current.scrollLeft = this.props.scrollPosition;
    this._containerRef.current.addEventListener('scroll', () => {
      this.setState({
        isUserScrolling: true,
      });

      this._throttleScroll();
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.scrollPosition !== this.props.scrollPosition) {
      this._containerRef.current.scrollLeft = this.props.scrollPosition;
    }

    if (!this.state.isUserScrolling && prevProps.activePage !== this.props.activePage) {
      const scroll = calculateScrollAmount(this.props.activePage, {
        totalPageCount: this.props.pages.length,
        hasInsideCovers: this.props.hasInsideCovers,
        imageWidth: this.props.imageWidth,
      });
      this.props.setScrollPosition(scroll);
      this._containerRef.current.scrollLeft = scroll;
    }
  }

  componentWillUnmount() {
    this.props.setScrollPosition(this._containerRef.current.scrollLeft);
    this._throttleScroll.cancel();
    this._containerRef.current.removeEventListener('scroll', this._throttleScroll);
  }

  getPage(n) {
    const props = this.props;
    const pages = props.pages;
    const pageIdx = (n + pages.length) % pages.length;
    return pages[pageIdx];
  }

  updatePage(pageIdx) {
    const props = this.props;
    if (props.pages) {
      const page = this.getPage(pageIdx);
      props.setActivePage(page.id);
      this.setState(state => ({
        zoom: {
          ...state.zoom,
          currentIdx: props.pages.indexOf(page),
          currentPage: page,
          nextPage: this.getPage(pageIdx + 1),
          prevPage: this.getPage(pageIdx + 1),
        },
      }));
    }
  }

  handleZoomActive = () => {
    const { productId, activePage } = this.props;
    const selectedPage = this.props.pages.find(page => page.id === activePage);
    const selectedPageIdx = this.props.pages.indexOf(selectedPage);

    trackZoomActivate(productId, selectedPageIdx);

    this.updatePage(selectedPageIdx);
    this.setState(state => ({
      zoom: {
        ...state.zoom,
        isActive: true,
      },
    }));
  };

  getLargerImage(imageUrl) {
    const queryRegex = /([?]).*/g;
    const queryString = imageUrl.match(queryRegex)[0];
    let imageQuery = qs.parse(queryString, { ignoreQueryPrefix: true });

    imageQuery.width = imageQuery.width ? imageQuery.width * 2 : null;
    imageQuery.w = imageQuery.w ? imageQuery.w * 2 : null;
    imageQuery = omitBy(imageQuery, isNil);
    imageQuery = qs.stringify(imageQuery, { addQueryPrefix: true });

    return imageUrl.replace(queryRegex, imageQuery);
  }

  handleZoomPrev = () => {
    const { productId } = this.props;
    const { currentIdx } = this.state.zoom;
    const pageIdx = currentIdx - 1;

    trackZoomPrev(productId, pageIdx);
    this.updatePage(pageIdx);
  };

  handleZoomNext = () => {
    const { productId } = this.props;
    const { currentIdx } = this.state.zoom;
    const pageIdx = currentIdx + 1;

    trackZoomNext(productId, pageIdx);
    this.updatePage(pageIdx);
  };

  handleZoomClose = () => {
    const { productId } = this.props;

    trackZoomClose(productId);
    this.setState(state => ({
      zoom: {
        ...state.zoom,
        isActive: false,
      },
    }));
  };

  render() {
    const props = this.props;
    return (
      <React.Fragment>
        <div
          className="scrolling-book-container"
          ref={this._containerRef}
          style={{ overflow: this.state.zoom.isActive && 'hidden' }}>
          <div className={cx('scrolling-book', { 'agitated-monkey': !this.state.hasUserScrolled })}>
            {props.pages.map((page, i, pages) => (
              <img
                key={i}
                src={page.url}
                alt={page.url}
                className={cx({
                  isSplit: hasMargins(i, props.hasInsideCovers, pages.length),
                })}
                style={{ width: props.imageWidth }}
              />
            ))}
          </div>
        </div>
        <button
          className="scrolling-book-zoom-icon"
          onClick={this.handleZoomActive}>
          <Icon icon="search" />
        </button>
        {this.state.zoom.isActive && (
          <div className="zoom">
            <Lightbox
              className="zoom-modal"
              mainSrc={this.getLargerImage(this.state.zoom.currentPage.url)}
              nextSrc={this.getLargerImage(this.state.zoom.nextPage.url)}
              prevSrc={this.getLargerImage(this.state.zoom.prevPage.url)}
              onCloseRequest={this.handleZoomClose}
              onMovePrevRequest={this.handleZoomPrev}
              onMoveNextRequest={this.handleZoomNext}
              animationDisabled
            />
          </div>
        )}
      </React.Fragment>
    );
  }
}

export default ScrollingBook;



// WEBPACK FOOTER //
// ./src/client/js/view/components/BookPreview/ScrollingBook/ScrollingBook.js